package com.tovtalk.di

import com.tovtalk.data.cache.LocalCacheManager
import com.tovtalk.data.cache.SessionVideoCache
import com.tovtalk.data.cache.VideoPreloadManager
import com.tovtalk.data.network.BandwidthEstimator
import com.tovtalk.data.remote.ApiClient
import com.lomdaat.ads.createAdsClient
import com.tovtalk.data.repository.AdsRepositoryImpl
import com.tovtalk.data.repository.AppRepositoryImpl
import com.tovtalk.data.repository.CommentRepositoryImpl
import com.tovtalk.data.repository.HashtagRepositoryImpl
import com.tovtalk.data.repository.NotificationRepositoryImpl
import com.tovtalk.data.repository.PostRepositoryImpl
import com.tovtalk.data.repository.RecentSearchesRepositoryImpl
import com.tovtalk.data.repository.UserRepositoryImpl
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.CommentRepository
import com.tovtalk.domain.repository.HashtagRepository
import com.tovtalk.domain.repository.NotificationRepository
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.RecentSearchesRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.domain.usecase.auth.LoginUseCase
import com.tovtalk.domain.usecase.auth.RegisterUseCase
import com.tovtalk.domain.usecase.post.GetDiscoverPostsUseCase
import com.tovtalk.domain.usecase.post.IncreaseViewCountUseCase
import com.tovtalk.domain.usecase.post.LikePostUseCase
import com.tovtalk.presentation.viewmodel.auth.AuthViewModel
import com.tovtalk.presentation.viewmodel.home.HomeViewModel
import com.tovtalk.presentation.viewmodel.profile.ProfileViewModel
import com.tovtalk.presentation.viewmodel.search.SearchViewModel
import com.tovtalk.utils.config.PlatformConfig
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import org.koin.core.module.Module
import org.koin.core.module.dsl.singleOf
import org.koin.dsl.bind
import org.koin.dsl.module

/**
 * Koin Modules
 */

/**
 * Database Module
 */
val databaseModule = module {
    single { com.tovtalk.data.local.LocalDataSource(get()) }
    
    single { 
         com.tovtalk.data.repository.SettingsRepositoryImpl(get(), get()) 
    } bind com.tovtalk.domain.repository.SettingsRepository::class
    
    single { 
         com.tovtalk.data.repository.UploadRepositoryImpl(get()) 
    } bind com.tovtalk.domain.repository.UploadRepository::class
    
    single(createdAtStart = true) {
        val settings = get<com.russhwolf.settings.Settings>()
        com.tovtalk.data.local.TokenManager.init(settings)
        com.tovtalk.data.local.TokenManager
    }
}

/**
 * Platform Specific Module
 */
expect val platformModule: Module

// ... (other modules unchanged)

/**
 * Network Module
 */
val networkModule = module {
    single { BandwidthEstimator() }
    single {
        ApiClient(
            baseUrl = PlatformConfig.getApiBaseUrl(),
            apiKey = PlatformConfig.getApiKey(),
            getAuthToken = { com.tovtalk.data.local.TokenManager.accessToken },
            bandwidthEstimator = get()
        )
    }
    single { com.tovtalk.presentation.viewmodel.auth.AuthSheetController() }
    single {
        com.tovtalk.domain.auth.AuthInterceptor(
            tokenManager = com.tovtalk.data.local.TokenManager,
            authSheet = get(),
        )
    }
    single { SessionVideoCache() }
    single {
        VideoPreloadManager(
            httpClient = get<ApiClient>().httpClient,
            bandwidth = get(),
            cache = get()
        )
    }
    single { LocalCacheManager(settings = get()) }
}

/**
 * Repository Module
 */
val repositoryModule = module {
    single<UserRepository> { UserRepositoryImpl(get(), get(), get()) }
    single<AppRepository> { AppRepositoryImpl(get()) }
    singleOf(::PostRepositoryImpl) bind PostRepository::class
    singleOf(::CommentRepositoryImpl) bind CommentRepository::class
    singleOf(::NotificationRepositoryImpl) bind NotificationRepository::class
    single<RecentSearchesRepository> { RecentSearchesRepositoryImpl(get()) }
    singleOf(::HashtagRepositoryImpl) bind HashtagRepository::class
    single { createAdsClient() }
    single<AdsRepository> {
        AdsRepositoryImpl(
            adsClient = get(),
            httpClient = get<ApiClient>().httpClient,
            identityProvider = get(),
            feedAdsEnabledForGuests = true,
            profileAdsEnabledForGuests = true,
        )
    }
}

/**
 * Use Case Module
 */
val useCaseModule = module {
    // Auth
    factory { LoginUseCase(get(), get()) }
    factory { RegisterUseCase(get()) }
    
    // Post
    factory { GetDiscoverPostsUseCase(get()) }
    factory { LikePostUseCase(get()) }
    factory { IncreaseViewCountUseCase(get()) }
}

/**
 * ViewModel Module
 */
val viewModelModule = module {
    factory { 
        AuthViewModel(
            loginUseCase = get(),
            registerUseCase = get(),
            userRepository = get(),
            settingsRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    single {
        HomeViewModel(
            getDiscoverPostsUseCase = get(),
            likePostUseCase = get(),
            increaseViewCountUseCase = get(),
            postRepository = get(),
            settingsRepository = get(),
            userRepository = get(),
            adsRepository = get(),
            appRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    factory {
        ProfileViewModel(
            userRepository = get(),
            postRepository = get(),
            uploadRepository = get(),
            adsRepository = get(),
            appRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    
    single {
        SearchViewModel(
            userRepository = get(),
            recentSearchesRepository = get(),
            hashtagRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    single {
        com.tovtalk.presentation.viewmodel.upload.UploadViewModel(get(), get(), get(), get())
    }
    
    single {
        com.tovtalk.presentation.viewmodel.chat.ChatListViewModel(
            chatRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    factory { 
        com.tovtalk.presentation.viewmodel.chat.ChatViewModel(
            chatRepository = get(),
            userRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    factory {
        com.tovtalk.presentation.viewmodel.comments.CommentsViewModel(
            commentRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
    
    single {
        com.tovtalk.presentation.viewmodel.notifications.NotificationViewModel(
            notificationRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }

    factory {
        com.tovtalk.presentation.viewmodel.onboarding.OnboardingViewModel(
            userRepository = get(),
            appRepository = get(),
            settingsRepository = get(),
            postRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }

    factory {
        com.tovtalk.presentation.viewmodel.post.PostDetailViewModel(
            postRepository = get(),
            userRepository = get(),
            scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
        )
    }
}

/**
 * All Shared Modules
 */
fun getSharedModules(): List<Module> = listOf(
    platformModule,
    databaseModule,
    networkModule,
    repositoryModule,
    useCaseModule,
    viewModelModule
)
